<script>
import { mapGetters } from 'vuex'
import ValidationStatusColumnStatusTooltip from './ValidationStatusColumnStatusTooltip.vue'
import ValidationStatusColumnMenuBadge from './ValidationStatusColumnMenuBadge.vue'
import ValidationResult from '../validationResult/ValidationResult.vue'
import DocumentDetails from '../documentDetails/DocumentDetails.vue'
import AttachmentsUploader from '@/components/common/AttachmentsUploader.vue'
import documentValidationDataTableJson from '@/data/json/document-validation/documentValidationDataTable.json'
import MergeCountHint from '@/components/document-validation/MergeCountHint.vue'
import AddCoverSheetButton from '@/components/document-validation/coverSheet/AddCoverSheetButton.vue'
import { CarrierType, DeliveryType, DocumentProductionStatus } from '@/enums'
import RecipientAddressBlock from '@/components/document-validation/documentValidationDataTable/RecipientAddressBlock.vue'

export default {
  components: {
    RecipientAddressBlock,
    AddCoverSheetButton,
    MergeCountHint,
    ValidationStatusColumnStatusTooltip,
    ValidationStatusColumnMenuBadge,
    ValidationResult,
    DocumentDetails,
    AttachmentsUploader
  },
  props: {
    order: { type: [Object, Array] }, // comes from documentValidation as Object, ordersWrapper as Array
    visibleDocumentStatus: { type: Object },
    visibleCostCenter: { type: Array },
    height: { type: Number, default: 540 },
    fetchOrders: { type: Function }, // from OrdersWrapper
    showOrderNumber: { type: Boolean, default: false },
    filterRules: { type: Object }
  },
  data: () => ({
    content: documentValidationDataTableJson,
    isValidationDialogOpen: false,
    validationResults: [],
    validationItem: null,
    pollingPendingDocuments: null,
    pollingPendingDocumentsInterval: 5000, // in milliseconds
    isErrorDialogOpen: false,
    isLoading: true,
    isCalling: false,
    isDeleting: false,
    deletedCount: 0,
    deletedOrderIds: [],
    orderExt: [], // extended Order
    selected: [], // selected items
    search: '', // the search text
    singleExpand: false, // single or multi expandable rows
    expanded: [], // the expanded items
    transitioned: [], // indicator for row-transitioning
    closeTimeouts: {}, // timeouts for row-expandables
    loadingDetails: [], // loading-animation indicator
    userActionRequired: {},
    menuAttrs: null
  }),
  computed: {
    DeliveryType() {
      return DeliveryType
    },
    CarrierType() {
      return CarrierType
    },
    DocumentProductionStatus() {
      return DocumentProductionStatus
    },
    isIterableOrder() {
      return Array.isArray(this.order)
    },
    ...mapGetters(['selectedOrganization']),
    hasMergeCounts() {
      return !!this.validationResults.find(x => x.mergeCount)
    },
    hasPending() {
      let pending = false
      if (
        this.validationResults.find(
          x => x.validationStatus === this.$enums.validationStatus.pending
        )
      ) {
        pending = true
      } else {
        // no pending documents ? stop the interval
        this.stopPolling()
      }
      return pending
    },
    hasCostCenter() {
      return this.validationResults.find(x => x.costCenter)
    },
    isPending() {
      return item =>
        item.validationStatus === this.$enums.validationStatus.pending
    },
    computedHeaders() {
      return [
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.parcel.number'),
          value: 'documentId',
          align: 'center',
          isHidden: true
        },
        {
          text: this.$t( 'component.documentValidation.documentValidationDataTable.table.order.number'),
          value: 'orderNumber',
          isHidden: !this.showOrderNumber
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.owner'),
          value: 'owner.lastName',
          isHidden: !this.selectedOrganization.OrganizationId || !this.selectedOrganization.IsAdmin
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.filename'),
          value: 'uploadFileName',
          width: 120,
          align: 'start'
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.merge.count'),
          value: 'mergeCount',
          isHidden: true
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.receiver'),
          value: 'recipientInfo.recipient',
          align: 'start'
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.carrier'),
          value: 'carrier',
          isHidden: !this.isIterableOrder
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.delivery.type'),
          value: 'deliveryType',
          isHidden: !this.isIterableOrder
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.production.status'),
          value: 'productionStatus',
          isHidden: !this.isIterableOrder
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.cost.center'),
          value: 'costCenter',
          align: 'center',
          isHidden: !this.hasCostCenter,
          filter: value => {
            if (this.visibleCostCenter !== undefined) {
              return this.visibleCostCenter.includes(value)
            } else {
              return true
            }
          }
        },
        {
          text: this.$t('component.documentValidation.documentValidationDataTable.table.actions'),
          value: 'validationStatus',
          align: 'center',
          width: 160,
          filter: value => {
            if (this.visibleDocumentStatus !== undefined) {
              // filter document by status
              return this.visibleDocumentStatus[value]
            } else {
              return true
            }
          }
        }
      ].filter(header => !header.isHidden)
    },
    selectedOrderIds() {
      return [].concat.apply(
        [],
        this.selected.map(selectedDocument => selectedDocument.orderId)
      )
    },
    canDelete() {
      return (
        this.validationResults.find(
          x => x.validationStatus === this.$enums.validationStatus.pending
        ) === undefined
      )
    }
  },
  watch: {
    isErrorDialogOpen(dialogState, dialogStateOld) {
      // refresh when dialog was closed
      // and documents are not inDeletion-progress
      if (dialogStateOld && !dialogState && !this.isDeleting) {
        if (this.fetchOrders) this.fetchOrders()
      }
    },
    validationResults(validationResultsNew, validationResultsOld) {
      // on deletion, do nothing
      if (this.isDeleting) return
      // reduce validationResult into a small array
      // Array({orderId: Integer, validationStatus: Array()})
      const reducer = (acc, d) => {
        const found = acc.find(a => a.orderId === d.orderId)
        const value = d.validationStatus
        if (!found) {
          acc.push({ orderId: d.orderId, validationStatus: [value] })
        } else {
          found.validationStatus.push(value)
        }
        return acc
      }

      const resultOld = validationResultsOld.reduce(reducer, [])
      const resultNew = validationResultsNew.reduce(reducer, [])

      let updatedOrderIds = []
      resultOld.forEach(oldElement => {
        // old result has pending documents
        if (
          oldElement.validationStatus.includes(
            this.$enums.validationStatus.pending
          )
        ) {
          // get all results that don't has pending documents
          if (
            !resultNew
              .find(eNew => eNew.orderId === oldElement.orderId)
              .validationStatus.includes(this.$enums.validationStatus.pending)
          )
            updatedOrderIds.push(oldElement.orderId)
        }
      })
      // are there updatedOrderIds ?
      if (updatedOrderIds.length > 0) {
        // update Orders
        this.$emit('update:updated', {
          orderIds: updatedOrderIds
        })
      }
    }
  },
  created() {
    this.orderExt = this.orderExt.concat(this.order) // make array from object
    // argument is cms api-endpoint
    this.$cmsService
      .fetchContent('documentValidationDataTablecontents')
      .then(response => {
        if (response !== '') {
          this.content = response
        }
      })
      .catch(() => {})
    this.getValidation().then(() => {
      // poll check if there are pending documents
      if (this.hasPending) {
        this.startPolling()
      }
    })
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    getTranslationFor(prefix, key) {
      return this.$cms.get(this.content, prefix + key)
    },
    getTypeKey(type, value) {
      for (const key in type) {
        if (type[key] === value) {
          return key
        }
      }
      return null
    },
    findOrderItemByDocumentId(orders, itemDocumentId) {
      if (this.isIterableOrder) {
        for (const order of orders) {
          for (const position of order?.orderPositions) {
            for (const document of position?.documents) {
              if (document?.documentId === itemDocumentId) {
                for (const orderPosition of order?.orderPositions) {
                  for (const document of orderPosition?.documents) {
                    if (document?.documentId === itemDocumentId) {
                      return document
                    }
                  }
                }
              }
            }
          }
        }
        return null
      }
      return null
    },
    addMenuAttrs(event) {
      this.menuAttrs = event
    },
    closeMenu() {
      this.menuAttrs.menu.click(this.menuAttrs.event)
    },
    //#region "ValidationResultDialog"
    openValidationResultDialog(validationItem) {
      // set validationItem for dialog
      this.validationItem = validationItem
      this.isValidationDialogOpen = true
      this.$refs.dvr.load(this.validationItem)
    },
    closeValidationResultDialog() {
      this.isValidationDialogOpen = false
    },
    /**
     * Gets called when the user updates PostageOnCover in ValidationResultDialog
     * @caller `ValidationResultDialog`
     * @param {event} event The `event`
     */
    documentUpdated(event) {
      if (event.updated) {
        event.document.validationStatus = this.$enums.validationStatus.pending
        this.startPolling()
      }
    },
    //#endregion "ValidationResultDialog"
    async deleteDocuments() {
      let documentIds = this.selected.reduce((acc, value) => {
        acc.push(value.documentId)
        return acc
      }, [])
      if (this.selected.length > 0) {
        this.isDeleting = true
        this.deletedOrderIds = Object.keys(
          this.selected.reduce(function(groups, item) {
            const val = item['orderId']
            groups[val] = groups[val] || []
            groups[val].push(item)
            return groups
          }, {})
        )
        await this.$documentsService
          .deleteDocuments({ documentIds: documentIds })
          .then(deleteResponse => {
            if (deleteResponse.documentIds) {
              for (var i = 0; i < deleteResponse.documentIds.length; i++) {
                let selectedItem = this.selected.find(
                  item => item.documentId === deleteResponse.documentIds[i]
                )
                this.deleteItem(selectedItem)
              }
              this.getValidation(false)
              // emit update
              this.$emit('update:updated', {
                count: deleteResponse.documentIds.length,
                orderIds: this.deletedOrderIds,
                source: 'DataTable'
              })
            }
          })
          .catch(() => {
            this.isErrorDialogOpen = true
          })
          .finally(() => {
            this.isDeleting = false
            this.returnDocuments()
          })
      }
    },
    deleteItem(item) {
      // delete from DataTable
      this.selected.splice(this.selected.indexOf(item), 1)
      // delete from validationResults
      const index = this.validationResults.indexOf(item)
      this.validationResults.splice(index, 1)
      // Delete from orderExt when there is no more documents for the uploadFile
      let uploadFileIdCount = this.validationResults.find(
        result => result.uploadFileId === item.uploadFileId
      )
      if (!uploadFileIdCount) this.deleteUploadFileFromOrderExt(item)
    },
    deleteUploadFileFromOrderExt(item) {
      // get index for order
      let orderExtIndex = this.orderExt.indexOf(
        this.orderExt.find(oExt => oExt.orderId === item.orderId)
      )
      // get orderPosition
      let orderExtPosition = this.orderExt[orderExtIndex].orderPositions.find(
        oP => oP.uploadFileId === item.uploadFileId
      )
      // get index for orderPosition
      let orderPositionIndex = this.orderExt[
        orderExtIndex
      ].orderPositions.indexOf(orderExtPosition)
      // delete from orderExt
      this.orderExt[orderExtIndex].orderPositions.splice(orderPositionIndex, 1)
    },
    getOrderDetails() {
      let orderIds = []
      let orderNumbers = []
      this.orderExt.forEach(order => {
        orderIds.push(order.orderId)
        orderNumbers.push({ [order.orderId]: order.orderNumber })
      })
      return {
        orderNumbers: orderNumbers,
        orderIds: orderIds
      }
    },
    getValidation(polling) {
      return new Promise(resolve => {
        this.isCalling = true
        let orderDetails = this.getOrderDetails()
        this.$uploadService
          .validation({ orderIds: orderDetails.orderIds })
          // .validation({ orderIds: [this.order.orderId] })
          .then(validationResponse => {
            this.validationResults = validationResponse.fileValidations
            this.userActionRequired = validationResponse.userActionRequired
            this.validationResults.forEach(result => {
              let orderNumber = orderDetails.orderNumbers.find(
                orderNumber => orderNumber[result.orderId]
              )
              let orderId = orderDetails.orderIds.find(
                orderId => orderId === result.orderId
              )
              let orderOwner = this.orderExt.find(
                order => order.orderId === orderId
              )
              result.orderNumber = orderNumber[result.orderId]
              result.orderId = orderId
              result.owner = orderOwner.owner || {
                firstName: '-',
                lastName: '-'
              }
            })
            // return documents to parent
            this.returnDocuments()
          })
          .catch(() => {
            this.stopPolling()
            this.validationResults = []
            this.returnDocuments()
          })
          .finally(() => {
            this.isCalling = false
            // return resolve-promise
            resolve(true)
            if (!polling) {
              // don't show loading / error messages on polling
              this.isLoading = false
            }
          })
      })
    },
    /**
     * Gets called when the user filter documents in DocumentValidation
     * @caller `DocumentValidation`
     * @param {Object} visibleDocumentStatus The `object` with documentStatus
     */
    filterDocuments(visibleDocumentStatus) {
      this.visibleDocumentStatus = visibleDocumentStatus
    },
    returnDocuments() {
      // return documents to parent
      this.$emit('returnDocuments', {
        validationResults: this.validationResults,
        userActionRequired: this.userActionRequired
      })
    },
    //#region "Polling"
    startPolling() {
      if (this.pollingPendingDocuments === null) {
        this.pollingPendingDocuments = setInterval(() => {
          // skip call when request is still calling (pending-call)
          if (!this.isCalling)
            this.getValidation(true).then(() => {
              if (!this.hasPending) {
                this.stopPolling()
                this.selected = [] // deselect all
              }
            })
        }, this.pollingPendingDocumentsInterval)
      }
    },
    stopPolling() {
      clearInterval(this.pollingPendingDocuments)
      this.pollingPendingDocuments = null
    },
    restartPolling(items) {
      // set pending-status
      items.forEach(item => {
        item.validationStatus = this.$enums.validationStatus.pending
      })
      this.returnDocuments()
      this.startPolling()
    },
    //#endregion "Polling"
    handleAddingCoverSheet(document) {
      const orderDocument = this.selected.find(
        item => item.documentId === document.documentId
      )
      orderDocument.coverSheetStatus = document.coverSheetStatus
    },
    handleFinalizingCoverSheet(documents) {
      this.restartPolling(documents)
    },
    handleClearSelection() {
      this.selected = []
    }
  }
}
</script>
<template>
  <div>
    <v-dialog v-model="isErrorDialogOpen" :max-width="500">
      <v-card>
        <DialogBaseColoredTitle color="error"
          >{{
            $t('component.documentValidation.documentValidationDataTable.error.dialog.header')
          }}
        </DialogBaseColoredTitle>
        <v-divider></v-divider>
        <DialogBaseText class="text-center primary--text">
          <v-row>
            <v-col>
              {{
                $t( 'component.documentValidation.documentValidationDataTable.error.dialog.content')
              }}
            </v-col>
          </v-row>
          <v-divider></v-divider>
        </DialogBaseText>
        <DialogBaseActions>
          <v-btn
            class="text-center px-8"
            color="primary"
            @click="isErrorDialogOpen = false"
          >
            {{ $t('button.close') }}
          </v-btn>
        </DialogBaseActions>
      </v-card>
    </v-dialog>
    <v-layout v-if="isLoading" row justify-center align-center class="mt-12">
      <v-progress-circular indeterminate></v-progress-circular>
    </v-layout>
    <v-overlay :value="isDeleting">
      <v-card light>
        <v-card-text class="text-center">
          <v-progress-linear indeterminate color="red"></v-progress-linear>
          <span>
            {{
              $t('component.documentValidation.documentValidationDataTable.overlay.deleting.documents')
            }}
          </span>
        </v-card-text>
      </v-card>
    </v-overlay>
    <MergeCountHint :is-visible="hasMergeCounts" />
    <v-data-table
      v-if="!isLoading"
      id="documentTable"
      ref="vDataTable"
      v-model="selected"
      :header-props="{
        sortByText: $t('global.sort.by.text')
      }"
      :footer-props="{
        'items-per-page-text': $t('global.items.per.page.text'),
        'items-per-page-all-text': $t('global.items.per.page.all.text'),
        'page-text': $t('global.page.text'),
        'items-per-page-options': [10, 25, 50, 100, -1]
      }"
      :headers="computedHeaders"
      :items="validationResults"
      :items-per-page="10"
      :show-select="
        order.hasOwnProperty('orderStatus') &&
          order.orderStatus < 400 &&
          canDelete
      "
      :height="height"
      :search="search"
      sort-by="documentId"
      item-key="documentId"
      elevation="0"
      :no-results-text="$t('global.no.data.text')"
      fixed-header
      calculate-widths>
      <template #top>
        <v-text-field
          id="search"
          v-model="search"
          :label="$t('component.documentValidation.documentValidationDataTable.search.label')"
          class="mx-4"
        ></v-text-field>
      </template>
      <template #[`item.data-table-select`]="{ item }">
        <v-checkbox
          :id="`upload_validation_select_${item.documentId}`"
          v-model="selected"
          :value="item"
          :disabled="isPending(item)">
        </v-checkbox>
      </template>
      <template #[`item.uploadFileName`]="{item}">
        <div style="position:relative">
          {{ item.uploadFileName }}
          <v-chip
            v-if="item.mergeCount"
            x-small
            color="success"
            class="round-chip"
            style="position:absolute;top:-12px;right:-15px;">
            + {{ item.mergeCount }}
          </v-chip>
        </div>
      </template>
      <template #[`item.recipientInfo.recipient`]="{ item }">
        <span
          v-if="
            !isPending(item) &&
              item.recipientInfo !== null &&
              item.recipientInfo !== undefined">
          <span style="position:relative;">
            <RecipientAddressBlock
              :item="item.recipientInfo"
              :is-country-visible="false"/>
          </span>
        </span>
        <span v-else> - </span>
      </template>
      <template #[`item.pageCount`]="{ item }">
        <span v-if="!isPending(item)">{{ item.pageCount }}</span>
        <span v-else>-</span>
      </template>
      <template #[`item.carrier`]="{ item }">
        <span v-if="isIterableOrder">
          {{
            getTranslationFor(
              'component.documentValidation.documentValidationDataTable.carrier.',
              getTypeKey(
                CarrierType,
                findOrderItemByDocumentId(order, item.documentId).carrier
              )
            )
          }}
        </span>
      </template>
      <template #[`item.deliveryType`]="{ item }">
        <span v-if="isIterableOrder">
          {{
            getTranslationFor(
              'component.documentValidation.documentValidationDataTable.deliveryType.',
              getTypeKey(
                DeliveryType,
                findOrderItemByDocumentId(order, item.documentId).deliveryType
              )
            )
          }}
        </span>
      </template>
      <template #[`item.productionStatus`]="{ item }">
        <span v-if="isIterableOrder">
          {{
            getTranslationFor(
              'component.documentValidation.documentValidationDataTable.productionStatus.',
              getTypeKey(
                DocumentProductionStatus,
                findOrderItemByDocumentId(order, item.documentId)
                  .productionStatus
              )
            )
          }}
        </span>
      </template>
      <template #[`item.costCenter`]="{ item }">
        <span class="wordBreak">{{ item.costCenter }}</span>
      </template>
      <template #[`item.validationStatus`]="{ item }">
        <div>
          <ValidationStatusColumnStatusTooltip
            :value="item.validationStatus"
            :item="item"
            :content="content"
            @click="openValidationResultDialog(item)" />
          <v-tooltip
            top
            content-class="dark_tip"
            class="text-xs-right" >
            <template #activator="{ on }">
              <v-btn
                :id="`upload_validation_preview_${item.documentId}`"
                :disabled="isPending(item)"
                icon
                large
                color="primary"
                class="d-inline"
                v-on="on"
                @click="openValidationResultDialog(item)" >
                <v-icon medium grey class="pa-0 ma-0">
                  mdi-file-search-outline
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('component.documentValidation.documentValidationDataTable.table.details.tooltip') }}</span>
          </v-tooltip>
          <v-menu
            :close-on-content-click="false"
            :nudge-width="500"
            :max-width="500"
            offset-x >
            <template #activator="{ on: menu, attrs }">
              <v-tooltip top content-class="dark_tip">
                <template #activator="{ on: tooltip }">
                  <ValidationStatusColumnMenuBadge
                    :item="item"
                    :attrs="{ tooltip: tooltip, menu: menu, attrs: attrs }"
                    :disabled="isPending(item)"
                    @created="addMenuAttrs"/>
                </template>
                <span>
                  {{ $t('component.documentValidation.documentValidationDataTable.table.parcelinformation.tooltip') }}
                </span>
              </v-tooltip>
            </template>
            <DocumentDetails
              :item="item"
              :order="order"
              :can-delete="canDelete"
              @itemsDeleted="
                restartPolling([item])
                $emit('update:updated', { orderIds: [item.orderId] })"
              @coverSheetDeleted="
                restartPolling([item])
                $emit('update:updated', { orderIds: [item.orderId] })
                closeMenu()"
              @close="closeMenu($event)"/>
          </v-menu>
        </div>
        <div>
          <v-progress-linear
            v-if="isPending(item)"
            indeterminate
            rounded
            height="3"
            color="orange">
          </v-progress-linear>
        </div>
      </template>
    </v-data-table>
    <div
      v-if="order.hasOwnProperty('orderStatus') && order.orderStatus < 400"
      class="table-footer-prepend d-flex pl-2 align-center">
      <AddCoverSheetButton
        v-if="!isLoading && canDelete"
        :is-cover-sheet-manager-dialog-open="true"
        :selected="selected"
        :order="order"
        @clear-selection="handleClearSelection"
        @added-coversheet="handleAddingCoverSheet"
        @finalized-coversheet="handleFinalizingCoverSheet"/>
      <AttachmentsUploader
        v-if="!isLoading && canDelete"
        :selected="selected"
        :order="order"
        @uploaded="
          restartPolling(selected)
          $emit('update:updated', {
            orderIds: selectedOrderIds})" />
      <div v-if="canDelete && !isLoading" class="ml-2">
        <v-tooltip content-class="dark_tip" top>
          <template #activator="{ on }">
            <v-btn
              id="upload_validation_delete_selected_documents"
              :disabled="selected.length <= 0"
              :loading="isDeleting"
              icon
              outlined
              color="error"
              v-on="on"
              @click="deleteDocuments()">
              <v-icon dark>mdi-delete-forever</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('component.documentValidation.documentValidationDataTable.tooltip.delete.icon') }}</span>
        </v-tooltip>
      </div>
    </div>
    <ValidationResult
      ref="dvr"
      :header="$t('component.documentValidation.documentValidationDataTable.preview.header')"
      :is-opened="isValidationDialogOpen"
      :close-dialog="closeValidationResultDialog"
      :order="validationItem"
      @updated="documentUpdated" >
    </ValidationResult>
  </div>
</template>
<style lang="scss" scoped>
.table-footer-prepend {
  margin-top: -58px;
  height: 58px;
}
</style>
<style lang="scss">
.v-badge--overlap .v-badge__badge {
  top: 0px !important;
}

.v-data-table .v-data-table__wrapper tbody tr {
  height: 56px;
}

.v-data-table .v-data-table__wrapper tbody td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
</style>